import { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, List, Pause, Play, X } from 'lucide-react';
import { useAuth } from '@greenrinse/ui';
import './tour.css';

/**
 * One step of the auto-play tour. Text comes from `tour.<app>.<id>.title|text`.
 * The tour shows, it never saves: `click` is only for links, tabs, rows and closing dialogs — never a Save/Submit button.
 */
export interface TourStep {
  id: string;
  path?: string;   // route this step happens on; omitted = wherever the previous click led (a dialog, a detail page)
  el?: string;     // CSS selector to spotlight; omitted or not found = centred popup
  click?: boolean; // the cursor clicks `el` after the text has been read
  type?: string;   // the cursor types this into `el` (an input)
}
export interface TourChapter { id: string; steps: TourStep[] }

interface State { i: number; playing: boolean; speed: number; jumped: boolean }
const SPEEDS = [1, 1.5, 2];
const START_EVENT = 'gr:tour';
const SEEN_EVENT = 'gr:tour-seen';
// One per app: the apps share an origin under /admin/, /partner/, /app/.
const SEEN_KEY = `gr.tourSeen${import.meta.env.BASE_URL}`;
const isDemo = !!import.meta.env.VITE_DEMO_ACCOUNTS;
const seen = () => { try { return localStorage.getItem(SEEN_KEY) === '1'; } catch { return true; } };
const markSeen = () => { try { localStorage.setItem(SEEN_KEY, '1'); } catch { /* storage may be unavailable */ } window.dispatchEvent(new Event(SEEN_EVENT)); };

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Sets an input's value the way React notices (it listens to `input` events on the native setter). */
function setNativeValue(el: HTMLInputElement, value: string) {
  Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set?.call(el, value);
  el.dispatchEvent(new Event('input', { bubbles: true }));
}

/** On screen? The side menu is a closed off-canvas drawer on phones: those steps show centred (the click still works). */
function onScreen(r: DOMRect | null): r is DOMRect {
  return !!r && r.width > 0 && r.height > 0 && r.right > 0 && r.left < window.innerWidth;
}

/** (Re)starts the tour from the first step after sign-in — for any "watch the tour" button. */
export const startTour = () => window.dispatchEvent(new Event(START_EVENT));

/** Top-bar "▶ Product tour" pill; shows a NEW badge until the tour has been played once in this browser. */
export function TourButton() {
  const { t } = useTranslation();
  const [isNew, setIsNew] = useState(() => !seen());
  useEffect(() => {
    const off = () => setIsNew(false);
    window.addEventListener(SEEN_EVENT, off);
    if (seen()) off(); // the auto-start may have fired before this mounted
    return () => window.removeEventListener(SEEN_EVENT, off);
  }, []);
  return (
    <button type="button" className="gr-tour-btn" data-tour="replay" onClick={startTour} title={t('tour.take')}>
      <span className="gr-tour-btn__play" aria-hidden><Play size={11} fill="currentColor" /></span>
      <span className="gr-tour-btn__label">{t('tour.product')}</span>
      {isNew && <span className="gr-tour-btn__new">{t('tour.new')}</span>}
    </button>
  );
}

/**
 * Auto-play guided tour: a cursor moves to each control, clicks or types, and a player bar offers chapters, back/next,
 * pause, speed and close. Started by `?tour=1` on any page (the landing page links to /login?tour=1) or by TourButton.
 * Progress lives in sessionStorage so it survives the sign-in and page changes.
 */
export function TourPlayer({ app, chapters }: { app: string; chapters: TourChapter[] }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();
  const storeKey = `gr.tour.${app}`;
  const steps = useMemo(() => chapters.flatMap((c, ci) => c.steps.map((s) => ({ ...s, ci }))), [chapters]);
  const afterLogin = Math.max(0, steps.findIndex((s) => s.path && s.path !== '/login'));
  const signInStep = Math.max(0, steps.findIndex((s) => s.path === '/login' && s.click)); // the step that clicks this app's demo pill (the next one presses Log in)

  const [st, setSt] = useState<State | null>(() => {
    try { return JSON.parse(sessionStorage.getItem(storeKey) ?? 'null') as State | null; } catch { return null; }
  });
  const [el, setEl] = useState<Element | null>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [action, setAction] = useState<string | null>(null);
  const [pressed, setPressed] = useState(false);
  const [timer, setTimer] = useState<number | null>(null); // ms the current step stays up: drives the countdown line
  const [menu, setMenu] = useState(false);
  const stRef = useRef(st); stRef.current = st;
  const go = (i: number) => setSt((s) => s && i >= 0 && i < steps.length ? { ...s, i, jumped: true } : s);
  const locRef = useRef(location.pathname); locRef.current = location.pathname;
  const cursorRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    try { if (st) sessionStorage.setItem(storeKey, JSON.stringify(st)); else sessionStorage.removeItem(storeKey); } catch { /* storage may be unavailable */ }
  }, [st, storeKey]);

  // Start: ?tour=1 (from the very first step, i.e. the sign-in) or the top-bar button (after sign-in).
  useEffect(() => {
    if (new URLSearchParams(location.search).get('tour') === '1') { markSeen(); setSt({ i: 0, playing: true, speed: 1, jumped: true }); }
    const onStart = () => { markSeen(); setSt({ i: afterLogin, playing: true, speed: stRef.current?.speed ?? 1, jumped: true }); };
    window.addEventListener(START_EVENT, onStart);
    return () => window.removeEventListener(START_EVENT, onStart);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Demo builds: play once by itself after the first sign-in in this browser.
  useEffect(() => {
    if (isDemo && user && !stRef.current && !seen() && locRef.current !== '/login') startTour();
  }, [user]);

  // Run the current step: go to its page, find its element, move the cursor, act, wait while it is read, advance.
  useEffect(() => {
    if (!st || loading) return;
    // Signed out (closed tab, logout, jumped ahead): do the one-click sign-in first, or the app would bounce to /login.
    if (!user && st.i >= afterLogin) { setSt({ ...st, i: signInStep, jumped: true }); return; }
    const step = steps[st.i];
    let alive = true;
    // Waits stop while paused and follow speed changes live.
    const wait = async (ms: number) => {
      for (let left = ms; left > 0 && alive;) { await sleep(100); if (stRef.current?.playing) left -= 100 * stRef.current.speed; }
    };
    (async () => {
      setEl(null); setAction(null); setMenu(false); setTimer(null);
      // A jump (back, chapter list, restart) into a step without its own path goes to the closest earlier path.
      const path = step.path ?? (st.jumped ? steps.slice(0, st.i).reverse().find((s) => s.path)?.path : undefined);
      if (path && locRef.current !== path) navigate(path);

      let target: Element | null = null;
      for (let n = 0; step.el && n < 40 && alive && !(target = document.querySelector(step.el)); n++) await sleep(150);
      if (!alive) return;
      target?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      setEl(target);
      await wait(300);
      if (!alive) return;

      if (step.type && target instanceof HTMLInputElement) {
        await wait(700);
        setAction(t('tour.typing', { text: step.type }));
        setNativeValue(target, '');
        for (let k = 1; k <= step.type.length && alive; k++) { setNativeValue(target, step.type.slice(0, k)); await wait(80); }
      }
      const title = t(`tour.${app}.${step.id}.title`), text = t(`tour.${app}.${step.id}.text`);
      if (step.click && target) setAction(t('tour.clicking'));
      const read = 1600 + (title.length + text.length) * 16; // ≈4 s a step at 1×, like the reference tour
      setTimer(read / (stRef.current?.speed ?? 1));
      await wait(read);
      if (!alive) return;

      if (step.click && target) {
        setPressed(true); await wait(200); setPressed(false);
        if (!alive) return;
        (target as HTMLElement).click();
        await wait(400);
        if (!alive) return;
      }
      if (st.i + 1 >= steps.length) setSt(null);
      else setSt((s) => s && { ...s, i: s.i + 1, jumped: false });
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [st?.i, st?.jumped, loading]);

  // Follow the element while the page scrolls, resizes or finishes rendering.
  useEffect(() => {
    if (!el) { setRect(null); return; }
    const update = () => setRect(el.isConnected ? el.getBoundingClientRect() : null);
    update();
    const id = window.setInterval(update, 150);
    return () => window.clearInterval(id);
  }, [el]);

  // Keyboard: Esc closes, Space pauses, arrows step.
  useEffect(() => {
    if (!st) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSt(null);
      else if (e.key === ' ') { e.preventDefault(); setSt((s) => s && { ...s, playing: !s.playing }); }
      else if (e.key === 'ArrowRight') go(st.i + 1);
      else if (e.key === 'ArrowLeft') go(st.i - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (!st) return null;
  const step = steps[st.i];
  const chapter = chapters[step.ci];
  const visible = onScreen(rect) ? rect : null;
  const vw = window.innerWidth, vh = window.innerHeight;
  const large = !!visible && visible.height > vh * 0.45; // a dialog, map or table: popup goes inside it, bar stays at the bottom
  const barOnTop = !!visible && !large && visible.bottom > vh - 140;
  const popW = Math.min(380, vw - 32);

  // Below the target, else above it, else inside it (dialogs, maps); centred under the target like the reference tour.
  let pop: CSSProperties = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: popW };
  if (visible) {
    const left = Math.min(Math.max(16, visible.left + visible.width / 2 - popW / 2), vw - popW - 16);
    if (visible.bottom + 280 < vh - (barOnTop ? 0 : 120)) pop = { top: visible.bottom + 16, left, width: popW };
    else if (visible.top - 280 > (barOnTop ? 120 : 0)) pop = { top: visible.top - 16, left, width: popW, transform: 'translateY(-100%)' };
    else pop = { top: Math.max(visible.top, 16) + 16, left: Math.min(visible.left + 16, vw - popW - 16), width: popW };
  }
  // Steps without a visible target keep the cursor where it was (so it doesn't sit on the centred popup).
  if (visible) cursorRef.current = large
    ? { x: visible.left + visible.width / 2, y: visible.top + visible.height / 2 }
    : { x: visible.left + Math.min(visible.width / 2, 60), y: visible.top + Math.min(visible.height / 2, 24) };
  const cursor = cursorRef.current ?? { x: vw / 2, y: vh - 40 };
  const firstOf = (ci: number) => steps.findIndex((s) => s.ci === ci);
  const inChapter = steps.filter((s) => s.ci === step.ci).length;

  return createPortal(
    <div className="gr-tp">
      <div className="gr-tp__block" />
      {visible
        ? <div className="gr-tp__spot" style={{ top: visible.top - 8, left: visible.left - 8, width: visible.width + 16, height: visible.height + 16 }} />
        : <div className="gr-tp__dim" />}

      <div className="gr-tp__popwrap" style={pop}>
        <div key={st.i} className="gr-tp__pop" role="dialog" aria-live="polite" aria-labelledby="gr-tp-title">
          <span className="gr-tp__status"><i className={st.playing ? 'is-on' : ''} />{st.playing ? t('tour.playing') : t('tour.paused')} · {t(`tour.${app}.ch.${chapter.id}`)}</span>
          <h2 id="gr-tp-title">{t(`tour.${app}.${step.id}.title`)}</h2>
          <p>{t(`tour.${app}.${step.id}.text`)}</p>
          {action && <div className="gr-tp__action"><Play size={12} fill="currentColor" aria-hidden />{action}</div>}
          {timer && <span key={timer} className="gr-tp__timer" style={{ animationDuration: `${timer}ms`, animationPlayState: st.playing ? 'running' : 'paused' }} />}
        </div>
      </div>

      <svg className={`gr-tp__cursor ${pressed ? 'is-pressed' : ''}`} style={{ transform: `translate(${cursor.x}px, ${cursor.y}px)` }} width="26" height="30" viewBox="0 0 26 30" aria-hidden>
        <path d="M2 2 L2 24 L8 18 L12 28 L16 26 L12 16 L20 16 Z" fill="#fff" stroke="#17261e" strokeWidth="2" strokeLinejoin="round" />
      </svg>

      <div className={`gr-tp__bar ${barOnTop ? 'gr-tp__bar--top' : ''}`} role="toolbar" aria-label={t('tour.take')}>
        <div className="gr-tp__info">
          <div className="gr-tp__head">
            <strong>{step.ci + 1}. {t(`tour.${app}.ch.${chapter.id}`)}</strong>
            <span>{t('tour.progress', { current: st.i + 1, total: steps.length })}</span>
            <span className="gr-tp__badge">{t('tour.demo')}</span>
          </div>
          <div className="gr-tp__segs">
            {chapters.map((c, ci) => (
              <span key={c.id} style={{ '--p': ci < step.ci ? 1 : ci > step.ci ? 0 : (st.i - firstOf(ci) + 1) / inChapter } as CSSProperties} />
            ))}
          </div>
        </div>
        <div className="gr-tp__ctrls">
          <button type="button" onClick={() => setMenu((m) => !m)} aria-label={t('tour.chapters')} aria-expanded={menu}><List size={22} /></button>
          <button type="button" onClick={() => go(st.i - 1)} disabled={st.i === 0} aria-label={t('tour.back')}><ChevronLeft size={24} /></button>
          <button type="button" className="gr-tp__play" onClick={() => setSt({ ...st, playing: !st.playing })} aria-label={st.playing ? t('tour.pause') : t('tour.play')}>
            {st.playing ? <Pause size={24} fill="currentColor" /> : <Play size={24} fill="currentColor" />}
          </button>
          <button type="button" onClick={() => go(st.i + 1)} disabled={st.i === steps.length - 1} aria-label={t('tour.next')}><ChevronRight size={24} /></button>
          <button type="button" className="gr-tp__speed" onClick={() => setSt({ ...st, speed: SPEEDS[(SPEEDS.indexOf(st.speed) + 1) % SPEEDS.length] })} aria-label={t('tour.speed')}>{st.speed}×</button>
          <button type="button" onClick={() => setSt(null)} aria-label={t('tour.close')}><X size={22} /></button>
        </div>
        {menu && (
          <ul className="gr-tp__menu">
            {chapters.map((c, ci) => (
              <li key={c.id}><button type="button" className={ci === step.ci ? 'is-current' : ''} onClick={() => go(firstOf(ci))}>{ci + 1}. {t(`tour.${app}.ch.${c.id}`)}</button></li>
            ))}
          </ul>
        )}
      </div>
    </div>,
    document.body,
  );
}
